import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget,DialogPositioningBehavior, DialogType, DialogWidget, ExpandoDirection, ExpandoWidget, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, TextFieldWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { sanitize } from '../../../../src/commonInfra/utils/Sanitize';
import { AddUsersButton, AddUsersButtonProps } from '../../../../src/userManagement/components/AddUsersButton';
import { SiteUsers } from '../../../../src/userManagement/services/resources/SiteUsers';
import { getAppStore } from '../../../app/AppStore';
import { siteUrlsSelector } from '../../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../../codegen/Localize';
import { openReactConfirmationDialog } from '../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { WindowLocationService } from '../../../commonInfra/services/WindowLocationService';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IUpdateSiteSettingsForSiteAdminParams, UpdateSiteSettingsForSiteAdminRequest } from '../../../ts/ServerApi/requests/UpdateSiteSettingsForSiteAdminRequest';
import { ISiteUser } from '../../../ts/ServerApi/types/ISiteUser';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import { SAMLAuthenticationProvider, SAMLProviderProps } from '../directives/SAMLAuthenticationProvider';
import {
    AuthenticationProviderType,
    IAuthenticationProvider,
    IOIDCAuthenticationProvider,
    is3rdParty,
    ISAMLAuthenticationProvider,
    ISiteAuthenticationSettings,
    SiteAuthenticationSettingsService  } from '../services/SiteAuthenticationSettingsService';
import { expandoStyle } from '../styles/SiteAuthenticationSettingsStyles';
import { ConfirmActionDialogSiteAuthenticationProviderEnable } from './ConfirmActionDialogSiteAuthenticationProviderEnable';
import { ScimSettings } from './ScimSettings';


export interface SiteAuthenticationSettingsProps {
    showEncryptionStatus: boolean;
    server: SessionManagementService;
    hideNameAndId: boolean;
    siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin;
    updateSiteSettingsForSiteAdmin?: () => void;
}

export interface SiteAuthenticationSettingsState {
    // SiteAuthenticationSettings in state and not taken from props, to be able to set radio buttons on mount
    siteAuthenticationSettings: ISiteAuthenticationSettings;
    isConnectedClientsVisible: boolean;
    isEmbeddedVizForSiteSamlSupported: boolean;
    isExternalProviderEnabled: boolean;
    isExternalProviderEnabledChanged: () => void;
    isTableauIdSupported: boolean;
    usersUrl: string;
    getProviderDisplayName: (provider: IAuthenticationProvider) => string;
    getProviderDescription: (provider: IAuthenticationProvider) => string;
    download: (evt: Event, url: string) => void;
    getHelp: (category?: string) => void;
    aboutToBeDisabled: IAuthenticationProvider[];
    aboutToLockMyselfOut: boolean;
    addUsersButtonProps: AddUsersButtonProps;
    defaultAuthEmbeddedViewsButtons: RadioButtonWithLabelOption<string>[];
    authenticationTypesProviderButtons: RadioButtonWithLabelOption<string>[];
    defaultAuthEmbeddedViewsRadioButtonIdx: number;
    authenticationTypesRadioButtonIdx: number;
    showEditConnection: boolean;
    samlProvider: ISAMLAuthenticationProvider;
    samlProviderProps: SAMLProviderProps;
}

interface SalesforceProviderConfigurerProps {
    provider: IOIDCAuthenticationProvider;
}

const SalesforceProviderConfigurer: React.FC<SalesforceProviderConfigurerProps> = props => {
    const ZERO_WIDTH = '\u200B';
    const SALESFORCE_DOMAIN_PLACEHOLDER = 'login.salesforce.com';

    const [dialogShown, setDialogShown] = React.useState<boolean>();
    const [domainFieldMessage, setDomainFieldMessage] = React.useState<string>(ZERO_WIDTH);
    const [domainFieldValid, setDomainFieldValid] = React.useState<boolean | 'pending'>();
    const [editedProvider, setEditedProvider] = React.useState<IOIDCAuthenticationProvider>({...props.provider});

    function cancel() {
        setDomainFieldMessage(ZERO_WIDTH);
        setDomainFieldValid(undefined);
        setEditedProvider({...props.provider});
        setDialogShown(false);
    }

    function apply() {
        setDomainFieldValid('pending');
        SiteAuthenticationSettingsService.saveOIDCProvider(editedProvider)
            .then(result => {
                if (result.errors && result.errors.length) {
                    setDomainFieldMessage(Messages.invalidValue());
                    setDomainFieldValid(false);
                } else {
                    Object.assign(props.provider, editedProvider);
                    setDomainFieldMessage(ZERO_WIDTH);
                    setDomainFieldValid(undefined);
                    setDialogShown(false);
                }
            });
    }

    return <>
        <div>{ Messages.ssoAuthTypeOIDC_description_salesforce({DOMAIN: props.provider.salesforceDomain || SALESFORCE_DOMAIN_PLACEHOLDER}) }</div>
        <TextLinkWidget
            children={Messages.salesforceEditMyDomain()}
            kind={TextLinkKind.Standard}
            onClick={e => {
                setDialogShown(true);
                e.preventDefault();
            }}
            testId='salesforce-provider-configurer-link'
        />
        {dialogShown
            ? <DialogWidget
                isModal={true}
                onCloseRequest={cancel}
                positioningBehavior={DialogPositioningBehavior.Draggable}
                showCloseButton={true}
                testId='salesforce-provider-configurer-dialog'
                title={Messages.salesforce()}
                type={DialogType.NormalWithTitleBar}
            >
                <TextFieldWidget
                    kind='line'
                    label={Messages.salesforceMyDomain()}
                    message={domainFieldMessage}
                    onClear={() => {
                        setDomainFieldMessage(ZERO_WIDTH);
                        setDomainFieldValid(undefined);
                        setEditedProvider({...editedProvider, salesforceDomain: ''});
                    }}
                    onChange={e => setEditedProvider({...editedProvider, salesforceDomain: e.target.value})}
                    placeholder={SALESFORCE_DOMAIN_PLACEHOLDER}
                    required={false}
                    style={{ width: 200 }}
                    testId='salesforce-domain-field-text'
                    valid={domainFieldValid}
                    value={editedProvider.salesforceDomain || ''}
                />
                <DialogFooterButtonsWidget
                    buttons=
                    {[
                        {
                            buttonType: ButtonType.Outline,
                            children: Messages.cancel(),
                            handleClick: cancel,
                            testId: 'salesforce-provider-configurer-dialog-footer-cancel',
                        },
                        {
                            buttonType: ButtonType.Outline,
                            children: Messages.apply(),
                            handleClick: apply,
                            testId: 'salesforce-provider-configurer-dialog-footer-apply',
                        },
                    ]}
                    testId='salesforce-provider-configurer-dialog-footer'
                />
            </DialogWidget>
            : null
        }
    </>;
};

export class SiteAuthenticationSettings extends React.Component<SiteAuthenticationSettingsProps, SiteAuthenticationSettingsState> {
    constructor(props: SiteAuthenticationSettingsProps) {
        super(props);
        this.state = {
            siteAuthenticationSettings: undefined,
            isConnectedClientsVisible: ServerSettingsUnauthenticated.isConnectedClientsVisible(),
            isEmbeddedVizForSiteSamlSupported: ServerSettingsUnauthenticated.isEmbeddedVizForSiteSamlSupported(),
            isExternalProviderEnabled: false,
            isExternalProviderEnabledChanged: () => {
                const newVal = !this.state.isExternalProviderEnabled;
                const provider = (newVal
                    ? this.state.siteAuthenticationSettings.externalProviders[0]
                    : this.state.siteAuthenticationSettings.defaultProvider);
                this.enableProvider(provider.id).then(() =>
                    this.setState({isExternalProviderEnabled: newVal})
                );
            },
            isTableauIdSupported: ServerSettingsUnauthenticated.isTableauIdSupported(),
            usersUrl: siteUrlsSelector(getAppStore().getState()).usersUrl(),
            getProviderDisplayName: (provider: IAuthenticationProvider) => {
                return provider
                    ? SiteAuthenticationSettingsService.getProviderDisplayName(provider)
                    : Messages.siteAuthenticationTypeSSO();
            },
            getProviderDescription: (provider: IAuthenticationProvider) => {
                switch(provider.type) {
                    case AuthenticationProviderType.oidc:
                      return Messages.ssoAuthTypeOIDC_description();
                    case AuthenticationProviderType.saml:
                      return Messages.ssoAuthTypeSAML_description();
                    case AuthenticationProviderType.default:
                      return Messages.ssoAuthTypeDefault_description();
                }
            },
            getHelp: (category?: string) => {
                const url = HelpService.getServerHelpUrlByCategory(category);
                window.open(url, '_blank');
            },
            download: (evt: Event, url: string) => {
                evt.preventDefault();
                WindowLocationService.download(url);
            },
            aboutToBeDisabled: [],
            aboutToLockMyselfOut: false,
            addUsersButtonProps: {
                isServerUsersSpace: false,
                addButtonText: Messages.siteAuthenticationStep6(),
                showSelectExistingUsersOption: true
            },
            defaultAuthEmbeddedViewsButtons: [],
            authenticationTypesProviderButtons: [],
            defaultAuthEmbeddedViewsRadioButtonIdx: 0,
            authenticationTypesRadioButtonIdx: 0,
            showEditConnection: false,
            samlProvider: undefined,
            samlProviderProps: {
                provider: undefined
            }
        };
    }

    public componentDidMount() {
        // Fetch data and initialize radio buttons
        this.setupInitialState();
    }

    public componentWillUnmount() {
        // Fix warning: Can't perform a React state update on an unmounted component
        // Once component is about to be unmounted, the callback functions used by its children should not work,
        // since the children will not be rendered anyway
        this.setState = () => { return; };
    }

    private setupInitialState() {
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings().then((siteAuthSettings: ISiteAuthenticationSettings) => {
            this.setState({siteAuthenticationSettings: siteAuthSettings});
            this.setState({defaultAuthEmbeddedViewsButtons: this.getProviderButtons()});
            this.setState({samlProvider: _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider});
            this.setState({samlProviderProps: {
                provider: this.state.samlProvider,
                updateSamlProvider: this.updateSamlProvider,
                updateEmbeddedOption: this.updateEmbeddedOption,
                updateAllowFullName: this.updateAllowFullName,
                updateFirstNameMapping: this.updateFirstNameMapping,
                updateLastNameMapping: this.updateLastNameMapping,
                updateFullNameMapping: this.updateFullNameMapping,
                updateEmailMapping: this.updateEmailMapping
            }});
            this.setState({authenticationTypesProviderButtons: this.getAuthenticationTypesProviderButtons()});
            this.setState({defaultAuthEmbeddedViewsRadioButtonIdx: this.state.defaultAuthEmbeddedViewsButtons.findIndex(button =>
                button.value === this.state.siteAuthenticationSettings.providerIdForEmbeddedViews)});
            this.setState({isExternalProviderEnabled: !!_.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => provider.isEnabled)});

            let provider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled));
            this.setState({authenticationTypesRadioButtonIdx: this.state.authenticationTypesProviderButtons.findIndex(button =>
                button.value === (provider ? provider.id : ''))});
        });
    }

    enableProvider(id: string) {
        return this.showEnableProviderWarning(id)
          .then(() => SiteAuthenticationSettingsService.enableProvider(id))
          .then(() => {
            SiteAuthenticationSettingsService.getSiteAuthenticationSettings().then((siteAuthenticationSettings: ISiteAuthenticationSettings) => {
              this.setState({siteAuthenticationSettings: siteAuthenticationSettings});
              this.setState({isExternalProviderEnabled: is3rdParty(id)});
              this.setState({authenticationTypesRadioButtonIdx: this.state.authenticationTypesProviderButtons.findIndex(button =>
                button.value === id)});
              this.setState({defaultAuthEmbeddedViewsButtons: this.getProviderButtons()});
            });
          });
    }

    showEnableProviderWarning(id: string): Promise<any> {
        return SiteUsers.instance.getById(getSessionManagementServiceInstance().sessionUserInfo().id).then((me: ISiteUser) => {
            this.setState({aboutToBeDisabled: _.filter(
                this.state.siteAuthenticationSettings.externalProviders,
                (provider: IAuthenticationProvider) => provider.isEnabled && provider.id !== id)});
            this.setState({aboutToLockMyselfOut: !!_.find(
                this.state.aboutToBeDisabled,
                (provider: IAuthenticationProvider) => !!me && provider.id === me.authenticationProviderId)});
        }).then(() => {
            if (this.state.aboutToBeDisabled.length === 0) {
                return Promise.resolve();
            }

            return openReactConfirmationDialog({
                title: Messages.ssoBeforeYouContinue_title(),
                message: '',
                paramsForAction: undefined,
                action: () => {
                    return Promise.resolve();
                },
                actionName: Messages.continueMessage(),
                dialogWidth: TabDialogWidth.width375px,
                bodyComponent: this.renderConfirmActionDialogSiteAuthenticationProviderEnable.bind(this)
            }).result;
        });
    }

    private updateSiteSettingsForSiteAdminRefreshTokenEnabled(checked: boolean) {
        const siteSettingsForSiteAdmin = this.props.siteSettingsForSiteAdmin;
        const siteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
        siteAdminSettings.refreshTokenEnabled = checked;
        const params: IUpdateSiteSettingsForSiteAdminParams = {
            id: (siteSettingsForSiteAdmin as any).id,
            siteAdminSettings: siteAdminSettings,
            subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
        };

        // Force a rerender since change comes from outside request, not state; too slow otherwise
        return this.notifyOnFail(WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params))).then(() => this.forceUpdate());
    }

    private updateSiteSettingsForSiteAdminBiometricsEnabled(checked: boolean) {
        const siteSettingsForSiteAdmin = this.props.siteSettingsForSiteAdmin;
        const siteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
        siteAdminSettings.mobileBiometricsEnabled = checked;
        const params: IUpdateSiteSettingsForSiteAdminParams = {
            id: (siteSettingsForSiteAdmin as any).id,
            siteAdminSettings: siteAdminSettings,
            subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
        };

        // Force a rerender since change comes from outside request, not state; too slow otherwise
        return this.notifyOnFail(WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params))).then(() => this.forceUpdate());
    }

    private notifyOnFail<TResult>(result: Promise<TResult>): Promise<TResult> {
        result.catch(() => {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
        });
        return result;
    }

    // Callback functions for child components to use

    private updateEmbeddedOption = (allow: boolean) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.allowEmbeddedAuthentication = allow;
        this.setState({samlProvider: samlProvider});
        // watchWithUndo equivalent: make call to service when callback is triggered by child component
        SiteAuthenticationSettingsService.setAllowEmbeddedAuthentication(samlProvider.id, allow);
    }

    private updateAllowFullName = (allow: boolean) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.useFullName = allow;
        this.setState({samlProvider: samlProvider});
    }

    private updateFirstNameMapping = (name: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.firstNameMapping = name;
        this.setState({samlProvider: samlProvider});
    }

    private updateLastNameMapping = (name: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.lastNameMapping = name;
        this.setState({samlProvider: samlProvider});
    }

    private updateFullNameMapping = (name: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.fullNameMapping = name;
        this.setState({samlProvider: samlProvider});
    }

    private updateEmailMapping = (email: string) => {
        let samlProvider = this.state.samlProvider;
        samlProvider.attributeMapping.emailMapping = email;
        this.setState({samlProvider: samlProvider});
    }

    private updateSamlProvider = () => {
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
            const updatedSamlProvider = _.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider;
            this.setState({ samlProvider: updatedSamlProvider, samlProviderProps: {...this.state.samlProviderProps, provider: updatedSamlProvider }});
        });
    }

    private setDefaultAuthenticationTypeAndUpdateRadioButtonIndex(id: string) {
        this.notifyOnFail(SiteAuthenticationSettingsService.setProviderIdForEmbeddedViews(id).then(() => {
            this.setState({defaultAuthEmbeddedViewsRadioButtonIdx: this.state.defaultAuthEmbeddedViewsButtons.findIndex(button => button.value === id)});
        }));
    }

    private renderConfirmActionDialogSiteAuthenticationProviderEnable(): JSX.Element {
        return (
        <ConfirmActionDialogSiteAuthenticationProviderEnable aboutToBeDisabled={this.state.aboutToBeDisabled} aboutToLockMyselfOut={this.state.aboutToLockMyselfOut}
            getProviderDisplayName={this.state.getProviderDisplayName.bind(this)} getHelp={this.state.getHelp.bind(this)}/>
        );
    }

    getProviderButtons(): RadioButtonWithLabelOption<string>[] {
        const providerButtons = [
            {
                label: Messages.userAuthSetting_AllowChoice(),
                testId: 'authentication-type-user-option-button',
                disabled: !this.state.isExternalProviderEnabled && !this.state.siteAuthenticationSettings.tabIdMfaProvider.isEnabled,
                value: undefined as any
            },
            {
                label: this.state.getProviderDisplayName(this.state.siteAuthenticationSettings.defaultProvider),
                testId: 'authentication-type-tableauid-button',
                value: this.state.siteAuthenticationSettings.defaultProvider.id
            }
        ];

        if (this.state.siteAuthenticationSettings.tabIdMfaProvider.isEnabled) {
            providerButtons.push(
                {
                    label: this.state.getProviderDisplayName(this.state.siteAuthenticationSettings.tabIdMfaProvider),
                    testId: 'authentication-type-tableauid-mfa-button',
                    value: this.state.siteAuthenticationSettings.tabIdMfaProvider.id
                }
            );
        }

        this.state.siteAuthenticationSettings.externalProviders.forEach((provider) => {
            if (provider.isEnabled) {
                providerButtons.push(
                    {
                        label: this.state.getProviderDisplayName(provider),
                        testId: 'authentication-type-sso-button',
                        value: provider.id
                    }
                );
            }
        });

        return providerButtons;
    }

    private getAuthenticationTypesProviderButtons(): RadioButtonWithLabelOption<string>[] {
        const providerButtons: RadioButtonWithLabelOption<string>[] = [];

        this.state.siteAuthenticationSettings.externalProviders.forEach((provider) => {
            const externalProviderButtonLabel = <>
                <div>{this.state.getProviderDisplayName(provider)}</div>
                {provider.type === AuthenticationProviderType.oidc && (provider as IOIDCAuthenticationProvider).alias === 'Salesforce'
                    ? <SalesforceProviderConfigurer provider={provider as IOIDCAuthenticationProvider} />
                    : <div dangerouslySetInnerHTML={{__html:
                        sanitize(this.state.getProviderDescription(provider))}}/>
                }
            </>;

            providerButtons.push(
                {
                    label: externalProviderButtonLabel,
                    testId: 'authentication-type-sso-button',
                    value: provider.id
                }
            );
        });

        return providerButtons;
    }

    // Main render functions

    private renderAuthenticationTypes(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.ssoAuthenticationTypes()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-msg">
                        {Messages.ssoAuthenticationTypes_description()}
                        &nbsp;
                        <TextLinkWidget onClick={() => this.state.getHelp('authenticationtypes')} testId='learn-more-link'>
                            {Messages.ssoLearnMore()}
                        </TextLinkWidget>
                    </div>
                    { this.state.siteAuthenticationSettings !== undefined
                    ? <div className="tb-settings-control">
                        <CheckBoxWithLabelWidget
                        label={this.state.getProviderDisplayName(this.state.siteAuthenticationSettings.defaultProvider)}
                        testId='authentication-type-tableau-checkbox'
                        disabled={true}
                        checked={true}
                        removeLeftMargin={true}/>
                        <div className="tb-checkbox-indent">
                            {this.state.getProviderDescription(this.state.siteAuthenticationSettings.defaultProvider)}
                        </div>
                    </div>
                    : null }
                    <div className="tb-settings-control">
                        <CheckBoxWithLabelWidget
                        label={Messages.ssoEnableAnAdditional()}
                        testId='enable-additional-authentication-checkbox'
                        checked={this.state.isExternalProviderEnabled}
                        removeLeftMargin={true}
                        handleChange={() => this.state.isExternalProviderEnabledChanged()}/>
                    </div>
                    <div className="tb-settings-control">
                        <div className="tb-checkbox-indent">
                            <RadioButtonWithLabelGroupWidget
                            buttonList={this.state.authenticationTypesProviderButtons}
                            checkedIndex={this.state.authenticationTypesRadioButtonIdx}
                            handleChange={this.enableProvider.bind(this)}/>
                            { this.state.samlProviderProps.provider !== undefined
                            ? <ExpandoWidget
                            // Placed separately from button label to be interactive, since labels cannot be without selecting the option
                            style={expandoStyle}
                            label={Messages.editConnection_action()}
                            testId={'edit-connection-toggle'}
                            isExpanded={this.state.showEditConnection}
                            onExpand={() => this.setState({showEditConnection: !this.state.showEditConnection})}
                            onCollapse={() => this.setState({showEditConnection: !this.state.showEditConnection})}
                            direction={ExpandoDirection.Down}>
                                <SAMLAuthenticationProvider {...this.state.samlProviderProps}/>
                            </ExpandoWidget>
                            : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderDefaultAuthenticationTypeForEmbeddedViews(): JSX.Element {
        return (
            this.state.isEmbeddedVizForSiteSamlSupported
            ? <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.siteAuthenticationEmbeddedTypeDesc()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-control">
                        <div className="tb-dialog-authentication-option">
                            <RadioButtonWithLabelGroupWidget
                            testId='default-authentication-embedded-views-buttons'
                            buttonList={this.state.defaultAuthEmbeddedViewsButtons}
                            checkedIndex={this.state.defaultAuthEmbeddedViewsRadioButtonIdx}
                            handleChange={this.setDefaultAuthenticationTypeAndUpdateRadioButtonIndex.bind(this)}/>
                        </div>
                    </div>
                </div>
            </div>
            : null);
    }

    private renderManageUsers(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.siteAuthenticationStep6()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-control">
                        <table>
                            <tbody>
                                <tr>
                                    <td className="tb-settings-msg">
                                        {Messages.siteAuthenticationManagerUsersDesc()}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <AddUsersButton {...this.state.addUsersButtonProps}/>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    private renderScimSettings(): JSX.Element {
        return (
            <ScimSettings/>
        );
    }

    private renderConnectedClients(): JSX.Element {
        return (
            this.state.isConnectedClientsVisible
            ? <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.siteAuthenticationConnectedClients()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-control">
                        <div className="tb-settings-msg">
                            {Messages.siteAuthenticationConnectedClientsDescription()}
                        </div>
                    </div>
                    { this.props.siteSettingsForSiteAdmin !== undefined
                    ? <CheckBoxWithLabelWidget
                        label={Messages.siteAuthenticationAllowConnectedClients()}
                        checked={(this.props.siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings).refreshTokenEnabled}
                        testId={'allow-connected-clients-checkbox'}
                        removeLeftMargin={true}
                        handleChange={this.updateSiteSettingsForSiteAdminRefreshTokenEnabled.bind(this)}/>
                    : null }
                </div>
            </div>
            : null
        );
    }

    private renderBiometricsMobile(): JSX.Element {
        return (
            FeatureFlags.showMobileBiometricsSetting
            ? <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.biometricsMobile()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-control">
                        <div className="tb-settings-msg">
                            {Messages.biometricsMobileDetails()}
                        </div>
                    </div>
                    { this.props.siteSettingsForSiteAdmin !== undefined
                    && (this.props.siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings).hasOwnProperty('mobileBiometricsEnabled')
                    ? <CheckBoxWithLabelWidget
                        label={Messages.enableBiometricsMobile()}
                        checked={(this.props.siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings).mobileBiometricsEnabled}
                        testId={'allow-mobile-biometrics-checkbox'}
                        removeLeftMargin={true}
                        handleChange={this.updateSiteSettingsForSiteAdminBiometricsEnabled.bind(this)}/>
                    : null }
                </div>
            </div>
            : null);
    }

    public render(): JSX.Element {
        return (
            <div className="tb-scroll">
                <div className="tb-settings-container">
                    <div className="tb-settings-section">
                        <div className="tb-site-authentication">
                            {this.renderAuthenticationTypes()}
                            {this.renderDefaultAuthenticationTypeForEmbeddedViews()}
                            {this.renderManageUsers()}
                            {this.renderScimSettings()}
                            {this.renderConnectedClients()}
                            {this.renderBiometricsMobile()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
